// Build a player-core Manifest for the CMS true-preview (docs/10 §2.4). One place
// owns the media→ManifestItem mapping so the playlist preview and any other
// engine surface resolve items identically to the backend's manifest builder:
//   - image / video → the session-fetchable file route (mediaFileUrl, api/media.ts)
//   - url           → the external page, verbatim
//   - html_*        → the file route with a content-version cache-buster (?v=…) on
//                     manifest type 'html' — the CACHED sandboxed-iframe type the
//                     backend emits (MEDIA_KIND_TO_MANIFEST_TYPE, docs/09 §B1).
//                     In the CMS the preview MediaSource is pass-through, so 'html'
//                     and 'url' resolve identically here; matching the backend's
//                     type keeps the two mappings from drifting apart again.
// The screen "Preview now" manifest comes straight from the backend
// (/v1/screens/:id/manifest-preview) already in this shape, so it does NOT use
// this builder — this is only for the playlist preview, which has no schedule.
// mediaFileUrl is api/media.ts's VITE_API_BASE-aware helper (reused, not duplicated)
// so this manifest's urls are already absolute in a deployed build; EnginePreview's
// PlainUrlMediaSource additionally rewrites via resolvePreviewMediaUrl for the
// backend-sourced screen-preview manifest, which this file never sees.

import { HTML_MEDIA_KINDS, MEDIA_KIND_TO_MANIFEST_TYPE, type Media, type MediaKind } from '@seencast/shared';
import type { Manifest, ManifestItem } from '@seencast/player-core';
import { mediaFileUrl } from '../api/media.ts';

const HTML_KINDS = new Set<string>(HTML_MEDIA_KINDS);
const HORIZON_MS = 24 * 60 * 60 * 1000;

/** A short, content-derived version tag (?v=) for html media — mirrors the backend's
 * `contentVersion` intent so editing a slide's fields flips the iframe src and busts its
 * cache. A tiny 32-bit hash keeps this dependency-free (djb2 over the render inputs). */
function contentVersion(media: Media): string {
  const input = `${media.kind}|${media.id}|${JSON.stringify(media.fields_json ?? {})}`;
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

/** Resolve one playlist row (an id + duration + fit) against its media into a manifest item,
 * or null when the media isn't loaded/known yet (the caller skips it). */
export function mediaToManifestItem(
  itemId: string,
  media: Media | undefined,
  durationMs: number,
  fit: ManifestItem['fit'],
): ManifestItem | null {
  if (!media) return null;
  const kind = media.kind as MediaKind;
  const type = MEDIA_KIND_TO_MANIFEST_TYPE[kind] ?? 'image';
  let url: string;
  if (HTML_KINDS.has(kind)) {
    url = `${mediaFileUrl(media.id)}?v=${contentVersion(media)}`;
  } else if (kind === 'url') {
    url = media.src_url ?? '';
  } else {
    url = mediaFileUrl(media.id);
  }
  return { id: itemId, type, url, duration_ms: durationMs, fit };
}

export interface PreviewRow {
  /** Stable id used as the manifest item id (playlist editor row key, or item id). */
  key: string;
  media_id: string;
  duration_ms: number;
  fit: ManifestItem['fit'];
}

/**
 * A minimal single-window manifest for a playlist loop (no schedule, no overlay).
 * One all-day window [now, now+24h) holds the resolved items, so the engine loops
 * them exactly as a screen would — real durations, real crossfades. Rows whose
 * media isn't loaded are skipped. `now` is injectable for deterministic tests.
 */
export function buildPlaylistManifest(
  rows: PreviewRow[],
  mediaById: Map<string, Media>,
  now: number = Date.now(),
): Manifest {
  const items = rows
    .map((r) => mediaToManifestItem(r.key, mediaById.get(r.media_id), r.duration_ms, r.fit))
    .filter((it): it is ManifestItem => it !== null);
  return {
    version: `preview-${items.length}`,
    min_player_version: '0.1.0',
    screen: { id: 'preview', orientation: 'landscape', timezone: 'UTC' },
    poll_interval_s: 30,
    valid_until: now + HORIZON_MS,
    timeline: [
      { from: now - 1000, to: now + HORIZON_MS, playlist_id: 'preview', items },
    ],
    fallback: { items: [] },
  };
}
